import { getCookie } from '@/util';
import { defineStore } from 'pinia';
import { Observable } from '@/observables';

// TODO: Add more roles?
export type Role = 'admin';

export interface User {
  id: number;
  uid?: string;
  provider?: string;
  username?: string;
  display_name: string;
  profile_image_url: string;
  created: Date;
  roles?: Role[];
  is_staff?: boolean;
}

interface UserStoreState {
  loading: boolean;
  user: User | undefined;
  userList: Map<number, User>;
  onUserLoadedObservable: Observable<User | undefined>;
}

interface Ownable {
  created_by: number;
}

export const useUserStore = defineStore('users', {
  state: (): UserStoreState => {
    return {
      loading: false,
      user: undefined,
      userList: new Map(),
      onUserLoadedObservable: new Observable(),
    };
  },
  getters: {
    isLoggedIn(): boolean {
      return this.user !== undefined;
    },
    isLoading(): boolean {
      return this.loading;
    },
    isAdmin(): boolean {
      if (this.user === undefined) {
        return false;
      }
      return this.user.is_staff || false;
    },
  },
  actions: {
    async fetchCurrentUser(): Promise<User | undefined> {
      this.loading = true;
      let resp = await fetch('/api/users/me', {
        headers: {
          'content-type': 'application/json',
        },
      });
      const user: User = await resp.json();
      this.loading = false;
      if (user.id !== undefined) {
        this.user = user;
        this.userList.set(user.id, user);
      } else {
        this.user = undefined;
      }
      this.onUserLoadedObservable.notifyObservers(this.user);
      return this.user;
    },
    async getCurrentUser(): Promise<User | undefined> {
      if (this.isLoggedIn) {
        return this.user;
      }
      return await this.fetchCurrentUser();
    },
    owns(item: Ownable): boolean {
      if (this.user === undefined) {
        return false;
      }
      return item.created_by === this.user.id;
    },
    async fetchUsers(): Promise<User[]> {
      this.loading = true;
      let resp = await fetch('/api/users', {
        headers: {
          'content-type': 'application/json',
        },
      });
      if (resp.status !== 200) {
        return [];
      }
      const users: User[] = await resp.json();
      this.loading = false;
      users.forEach((u) => {
        this.userList.set(u.id, u);
      });
      return users;
    },
    async fetchUser(id: number): Promise<User | undefined> {
      this.loading = true;
      let resp = await fetch(`/api/users/${id}/`, {
        headers: {
          'content-type': 'application/json',
        },
      });
      const user = await resp.json();
      this.userList.set(user.id, user);
      return user;
    },
    async getUser(id: number): Promise<User | undefined> {
      if (this.userList.has(id)) {
        return this.userList.get(id);
      }
      return await this.fetchUser(id);
    },
    async toggleAdmin(id: number): Promise<void> {
      let resp = await fetch(`/api/users/${id}/toggle_admin/`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken')!,
        },
      });
    },
  },
});
